/**
 * Botanix ERP — Shared Pricing Hook
 * Fetches live rates from Dynamic Pricing API
 * Used by: RoomStayBooking, CorporateBooking, WeddingBooking, GroupBooking
 */
import { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

export function usePricing() {
 const [rates, setRates] = useState([]);
 const [rules, setRules] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');

 const load = useCallback(async () => {
 try {
 const [r, ru] = await Promise.all([
 fetch(`${API}/pricing/current-rates`).then(x=>x.json()),
 fetch(`${API}/pricing/rules`).then(x=>x.json()),
 ]);
 setRates(Array.isArray(r) ? r : []);
 setRules(Array.isArray(ru) ? ru.filter(r=>r.is_active) : []);
 } catch(e) {
 setError(e.message);
 } finally {
 setLoading(false);
 }
 }, []);

 useEffect(() => { load(); }, [load]);

 // Get base rate for a room type
 const getRoomRate = useCallback((roomTypeId, checkInDate) => {
 const rate = rates.find(r => r.room_type_id === parseInt(roomTypeId));
 if (!rate) return 0;

 let finalRate = parseFloat(rate.base_rate || rate.today_rate || 0);

 // Apply rules
 if (checkInDate) {
 const date = new Date(checkInDate);
 const dayOfWeek = date.getDay(); // 0=Sun, 6=Sat

 rules.forEach(rule => {
 let applies = false;
 const adj = parseFloat(rule.adjustment_value || 0);

 if (rule.rule_type === 'weekend' && rule.days_of_week) {
 const days = rule.days_of_week.split(',').map(Number);
 applies = days.includes(dayOfWeek);
 } else if (rule.rule_type === 'seasonal' && rule.start_date && rule.end_date) {
 applies = date >= new Date(rule.start_date) && date <= new Date(rule.end_date);
 } else if (rule.rule_type === 'early_bird' && rule.advance_days_min) {
 const daysAhead = Math.ceil((date - new Date()) / 86400000);
 applies = daysAhead >= parseInt(rule.advance_days_min);
 } else if (rule.rule_type === 'last_minute' && rule.advance_days_max) {
 const daysAhead = Math.ceil((date - new Date()) / 86400000);
 applies = daysAhead <= parseInt(rule.advance_days_max);
 }

 if (applies) {
 if (rule.adjustment_type === 'percentage') {
 finalRate = finalRate * (1 + adj/100);
 } else if (rule.adjustment_type === 'fixed') {
 finalRate = finalRate + adj;
 }
 }
 });
 }

 // Apply min/max bounds if set
 rules.forEach(rule => {
 if (rule.min_rate && finalRate < parseFloat(rule.min_rate)) finalRate = parseFloat(rule.min_rate);
 if (rule.max_rate && finalRate > parseFloat(rule.max_rate)) finalRate = parseFloat(rule.max_rate);
 });

 return Math.round(finalRate);
 }, [rates, rules]);

 // Get corporate/day picnic rates from pricing rules
 const getCorporateRates = useCallback(() => {
 // Look for corporate-specific rules or return defaults
 const corpRule = rules.find(r => r.rule_name?.toLowerCase().includes('corporate'));
 return {
 standard: corpRule ? parseFloat(corpRule.adjustment_value || 1800) : 1800,
 premium: 2800,
 };
 }, [rules]);

 return { rates, rules, loading, error, getRoomRate, getCorporateRates, reload: load };
}

// Standalone function to get current rates (for non-hook usage)
export async function fetchCurrentRates() {
 try {
 const [rates, rules] = await Promise.all([
 fetch(`${API}/pricing/current-rates`).then(x=>x.json()),
 fetch(`${API}/pricing/rules`).then(x=>x.json()),
 ]);
 return { rates: Array.isArray(rates)?rates:[], rules: Array.isArray(rules)?rules:[] };
 } catch {
 return { rates: [], rules: [] };
 }
}
